import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Users, Phone, Mail, MapPin, X, Calendar, AlertCircle, AlertTriangle, Clock, LogOut as LogOutIcon, MessageSquarePlus, DoorOpen, CheckCircle2, XCircle, Trash2, ChevronDown, ChevronRight, Home, RotateCcw } from "lucide-react";
import api, { getToken, clearSession, getLevel } from "@/lib/api";
import { formatLastFirst } from "@/lib/utils";
import { toast } from "sonner";
import PayrollPanel from "@/components/PayrollPanel";

function fmtPretty(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

const STATUS_PILL = {
  Present: "bg-emerald-100 text-emerald-800",
  Late: "bg-amber-100 text-amber-800",
  "Late Arrival": "bg-orange-100 text-orange-800",
  "Signed Out": "bg-blue-100 text-blue-800",
  Absent: "bg-red-600 text-white",
};

const GROUP_DIVISION_KEYWORDS = ["mariner", "skipper", "navigator"];

function isGroupDivision(name) {
  if (!name) return false;
  const lc = String(name).toLowerCase();
  return GROUP_DIVISION_KEYWORDS.some((kw) => lc.includes(kw));
}

// Natural sort: extracts trailing/embedded numbers so "Bunk 2" < "Bunk 10".
function naturalCmp(a, b) {
  const ax = String(a || "").toLowerCase();
  const bx = String(b || "").toLowerCase();
  const re = /(\d+)|(\D+)/g;
  const at = ax.match(re) || [];
  const bt = bx.match(re) || [];
  const n = Math.min(at.length, bt.length);
  for (let i = 0; i < n; i++) {
    const x = at[i];
    const y = bt[i];
    const xn = /^\d+$/.test(x);
    const yn = /^\d+$/.test(y);
    if (xn && yn) {
      const diff = parseInt(x, 10) - parseInt(y, 10);
      if (diff !== 0) return diff;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return at.length - bt.length;
}

function groupMembersByBunk(members) {
  const map = new Map();
  for (const m of members || []) {
    const key = (m.bunk_name && String(m.bunk_name).trim()) || "__unassigned__";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  const bunks = [];
  let unassigned = null;
  for (const [key, list] of map.entries()) {
    list.sort((a, b) => naturalCmp(a.name, b.name));
    if (key === "__unassigned__") {
      unassigned = { key, label: "Unassigned", members: list };
    } else {
      bunks.push({ key, label: key, members: list });
    }
  }
  bunks.sort((a, b) => naturalCmp(a.label, b.label));
  if (unassigned) bunks.push(unassigned);
  return bunks;
}

function Field({ label, value, testId, full = false }) {
  return (
    <div className={full ? "col-span-2 sm:col-span-4" : ""}>
      <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500 font-bold">{label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate" data-testid={testId}>{value}</div>
    </div>
  );
}

function StaffDetailModal({ crewId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const isAdmin = ["admin", "leadership"].includes(getLevel());

  // Notes form state
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Leave-early form state
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveReason, setLeaveReason] = useState("");
  const [signerName, setSignerName] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [submittingLeave, setSubmittingLeave] = useState(false);

  // Termination is a permanent state per admin's explicit request — no
  // reinstate/terminate UI actions. The modal shows a READ-ONLY banner
  // when a staffer is terminated (see the JSX below) so payroll can see
  // the status + date + reason without any button that could change it.


  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/staff-cards/${crewId}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) toast.error(e?.response?.data?.detail || "Could not load profile"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [crewId, refreshKey]);

  // Load supervisor names whenever the leave form opens — based on the staff member's division
  useEffect(() => {
    if (!leaveOpen || !data) return;
    // Map division name → slug. We use a quick keyword lookup.
    const div = (data.division || "").toLowerCase();
    let slug = null;
    if (div.includes("mariner")) slug = "mariners";
    else if (div.includes("skipper")) slug = "skippers";
    else if (div.includes("navigator")) slug = "navigators";
    else if (div.includes("specialit") || div.includes("specialty")) slug = "specialty";
    else if (div.includes("pool") || div.includes("aquatic")) slug = "pool";
    else if (div.includes("athletic") || div.includes("sport")) slug = "athletics";
    else if (div.includes("boat")) slug = "boating";
    else if (div.includes("dining") || div.includes("kitchen")) slug = "dining";
    else if (div.includes("health") || div.includes("medical") || div.includes("nurse")) slug = "nurses";
    else if (div.includes("mainten") || div.includes("facilit")) slug = "maintenance";
    else if (div.includes("office") || div.includes("reception")) slug = "office";
    else if (div.includes("supervisor") || div.includes("director")) slug = "supervisors";
    if (!slug) { setSupervisors([]); return; }
    api.get(`/divisions/${slug}/supervisors`).then((r) => setSupervisors(r.data.supervisors || []))
      .catch(() => setSupervisors([]));
  }, [leaveOpen, data]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      await api.post(`/staff-cards/${crewId}/notes`, { text });
      setNoteText("");
      toast.success("Note saved");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save note"); }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/staff-cards/${crewId}/notes/${noteId}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  // Tabs inside the modal
  const [activeTab, setActiveTab] = useState("overview");

  // Planned-absence add form state
  const [paOpen, setPaOpen] = useState(false);
  const [paMode, setPaMode] = useState("single"); // "single" | "range"
  const [paDate, setPaDate] = useState("");
  const [paEndDate, setPaEndDate] = useState("");
  const [paReason, setPaReason] = useState("");
  const [paLabel, setPaLabel] = useState("");
  const [savingPa, setSavingPa] = useState(false);

  const addPlannedAbsence = async () => {
    if (!paDate) { toast.error("Please pick a start date"); return; }
    if (paMode === "range") {
      if (!paEndDate) { toast.error("Please pick an end date"); return; }
      if (paEndDate < paDate) { toast.error("End date must be on or after start date"); return; }
    }
    setSavingPa(true);
    try {
      const r = await api.post(`/staff-cards/${crewId}/planned-absences`, {
        date: paDate,
        end_date: paMode === "range" ? paEndDate : null,
        reason: paReason.trim(),
        label: paLabel.trim() || null,
        category: "personal",
      });
      const count = r.data?.count || 1;
      toast.success(count > 1 ? `${count} planned absences added` : "Planned absence added");
      setPaOpen(false); setPaMode("single"); setPaDate(""); setPaEndDate(""); setPaReason(""); setPaLabel("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setSavingPa(false); }
  };

  const removePlannedAbsence = async (date) => {
    if (!window.confirm(`Remove planned absence on ${date}?`)) return;
    try {
      await api.delete(`/staff-cards/${crewId}/planned-absences/${date}`);
      toast.success("Removed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  // Planned-lateness add form state. Date + optional reason/label —
  // no time required (supervisors don't always know when staff will arrive).
  const [plOpen, setPlOpen] = useState(false);
  const [plDate, setPlDate] = useState("");
  const [plReason, setPlReason] = useState("");
  const [plLabel, setPlLabel] = useState("");
  const [savingPl, setSavingPl] = useState(false);

  const addPlannedLateness = async () => {
    // Read the date LIVE from the DOM in addition to React state — covers
    // the race where the user clicks Save immediately after picking a date
    // before React has received the onChange event.
    let dateVal = plDate;
    try {
      const dateEl = document.querySelector('[data-testid="pl-date"]');
      if (dateEl && dateEl.value) dateVal = dateEl.value;
    } catch { /* fall back to React state */ }

    if (!dateVal) { toast.error("Please pick a date"); return; }
    setSavingPl(true);
    try {
      await api.post(`/staff-cards/${crewId}/planned-latenesses`, {
        date: dateVal,
        reason: plReason.trim(),
        label: plLabel.trim() || null,
      });
      toast.success("Planned lateness added");
      setPlOpen(false); setPlDate(""); setPlReason(""); setPlLabel("");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg = detail || e?.message || "Could not add";
      if (status === 404) msg = "Planned-lateness endpoint missing on the server. Please redeploy.";
      if (status === 401) msg = "Your session expired. Please sign back in.";
      if (status === 403) msg = detail || "You don't have permission to add lateness for this staff member.";
      toast.error(msg);
    }
    finally { setSavingPl(false); }
  };

  const removePlannedLateness = async (date) => {
    if (!window.confirm(`Remove planned lateness on ${date}?`)) return;
    try {
      await api.delete(`/staff-cards/${crewId}/planned-latenesses/${date}`);
      toast.success("Removed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  // ---- Mark Absent (sick / callout) modal ----
  const [maOpen, setMaOpen] = useState(false);
  const [maMode, setMaMode] = useState("today");          // "today" | "range"
  const [maStart, setMaStart] = useState("");
  const [maEnd, setMaEnd] = useState("");
  const [maLabel, setMaLabel] = useState("Out sick — illness");
  const [maReason, setMaReason] = useState("");
  const [savingMa, setSavingMa] = useState(false);

  const todayISO = () => new Date().toISOString().slice(0, 10);
  const openMarkAbsent = () => {
    setMaMode("today");
    const t = todayISO();
    setMaStart(t); setMaEnd(t);
    setMaLabel("Out sick — illness");
    setMaReason("");
    setMaOpen(true);
  };

  const submitMarkAbsent = async () => {
    setSavingMa(true);
    try {
      const body = { mode: maMode, label: maLabel.trim() || "Out sick — illness", reason: maReason.trim() };
      if (maMode === "range") {
        if (!maStart || !maEnd) { toast.error("Pick a start and end date"); setSavingMa(false); return; }
        body.start_date = maStart;
        body.end_date = maEnd;
      }
      const r = await api.post(`/staff-cards/${crewId}/mark-absent`, body);
      const n = (r.data?.dates || []).length;
      toast.success(`Marked absent for ${n} day${n === 1 ? "" : "s"}`);
      setMaOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark absent");
    } finally { setSavingMa(false); }
  };

  // ---- Mark Present (admin / supervisor override) ----
  const [savingPresent, setSavingPresent] = useState(false);
  const [mpOpen, setMpOpen] = useState(false);
  const [mpDate, setMpDate] = useState(() => new Date().toISOString().slice(0, 10));
  const openMarkPresent = () => {
    setMpDate(new Date().toISOString().slice(0, 10));
    setMpOpen(true);
  };
  const submitMarkPresent = async () => {
    if (savingPresent) return;
    setSavingPresent(true);
    try {
      const body = mpDate ? { date: mpDate } : {};
      const r = await api.post(`/staff-cards/${crewId}/mark-present`, body);
      toast.success(
        r.data?.overrode_planned_absence
          ? `Marked Present for ${mpDate} (overrode planned absence)`
          : `Marked Present for ${mpDate}`
      );
      setMpOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark present");
    } finally { setSavingPresent(false); }
  };

  // ---- Log Left Early (with exact time) ----
  // Small inline popover next to Mark Absent / Mark Present. Time defaults
  // to "now" so a supervisor can just click Save.
  const nowHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const [leOpen, setLeOpen] = useState(false);
  const [leTime, setLeTime] = useState(nowHHMM());
  const [leReason, setLeReasonInput] = useState("");
  const [savingLe, setSavingLe] = useState(false);
  const openLogLeftEarly = () => {
    setLeTime(nowHHMM());
    setLeReasonInput("");
    setLeOpen(true);
    setAlOpen(false);
  };
  const submitLogLeftEarly = async () => {
    if (!leTime) { toast.error("Pick a time"); return; }
    setSavingLe(true);
    try {
      await api.post(`/staff-cards/${crewId}/log-left-early`, {
        time: leTime,
        reason: leReason.trim(),
      });
      toast.success(`Logged Left Early @ ${leTime}`);
      setLeOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log left-early");
    } finally { setSavingLe(false); }
  };

  // ---- Log Arrive Late (with exact time) ----
  const [alOpen, setAlOpen] = useState(false);
  const [alTime, setAlTime] = useState(nowHHMM());
  const [alReason, setAlReasonInput] = useState("");
  const [savingAl, setSavingAl] = useState(false);
  const openLogArriveLate = () => {
    setAlTime(nowHHMM());
    setAlReasonInput("");
    setAlOpen(true);
    setLeOpen(false);
  };
  const submitLogArriveLate = async () => {
    if (!alTime) { toast.error("Pick a time"); return; }
    setSavingAl(true);
    try {
      const r = await api.post(`/staff-cards/${crewId}/log-arrive-late`, {
        time: alTime,
        reason: alReason.trim(),
      });
      toast.success(`Logged Arrive Late @ ${alTime} — ${r.data?.status || "Late"}`);
      setAlOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log arrive-late");
    } finally { setSavingAl(false); }
  };


  const submitLeaveEarly = async (decision) => {
    const reason = leaveReason.trim();
    if (!reason) { toast.error("Please enter a reason."); return; }
    if (!signerName) { toast.error("Please select your name to sign."); return; }
    setSubmittingLeave(true);
    try {
      await api.post(`/staff-cards/${crewId}/leave-early`, { reason, decision, signer_name: signerName });
      toast.success(decision === "approved" ? `Approved & signed by ${signerName}.` : `Denied by ${signerName}.`);
      setLeaveOpen(false);
      setLeaveReason("");
      setSignerName("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSubmittingLeave(false); }
  };

  const todayLeaveEarly = data?.leave_early_today;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm p-4 flex items-center justify-center" data-testid="staff-detail-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            {data?.photo_base64 ? (
              <img
                src={`data:image/jpeg;base64,${data.photo_base64}`}
                alt=""
                className="w-20 h-20 rounded-full object-cover border-2 border-white/60 shadow-md shrink-0"
                data-testid="staff-detail-avatar"
              />
            ) : data?.photo_thumb_base64 ? (
              <img
                src={`data:image/jpeg;base64,${data.photo_thumb_base64}`}
                alt=""
                className="w-20 h-20 rounded-full object-cover border-2 border-white/60 shadow-md shrink-0"
                data-testid="staff-detail-avatar"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full bg-white/15 border-2 border-white/40 flex items-center justify-center font-bold text-white text-2xl shrink-0"
                aria-hidden="true"
              >
                {(data?.name || "?")
                  .split(" ")
                  .map((p) => p[0])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-white/70 font-semibold">Staff Profile</div>
              <div className="font-display text-xl font-bold truncate">{data?.name || (loading ? "Loading…" : "Unknown")}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 shrink-0" data-testid="close-staff-detail">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading profile…</div>
        ) : data ? (
          <div className="overflow-y-auto p-4 sm:p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {data.division && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100"><MapPin className="w-4 h-4 text-blue-600" /><span className="text-slate-600">Division:</span><span className="font-semibold text-slate-900">{data.division}</span></div>
              )}
              {data.position && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100"><Users className="w-4 h-4 text-blue-600" /><span className="text-slate-600">Position:</span><span className="font-semibold text-slate-900">{data.position}</span></div>
              )}
              {data.phone && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100"><Phone className="w-4 h-4 text-blue-600" /><span className="font-mono text-slate-900 text-sm">{data.phone}</span></div>
              )}
              {data.email && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100"><Mail className="w-4 h-4 text-blue-600" /><span className="font-mono text-slate-900 text-sm truncate">{data.email}</span></div>
              )}
            </div>

            {/* Mark Absent / Mark Present / Left Early / Arrive Late button row —
                hidden for terminated staff since their record is READ-ONLY
                for payroll. */}
            {!data.is_terminated && (
            <div className="space-y-2" data-testid="mark-absent-row">
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={openMarkAbsent}
                        className="btn-touch inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-100 hover:bg-red-200 text-red-800 text-sm font-bold border border-red-200"
                        data-testid="mark-absent-btn">
                  Mark Absent
                </button>
                <button onClick={openMarkPresent}
                        disabled={savingPresent}
                        className="btn-touch inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-100 hover:bg-emerald-200 disabled:opacity-60 text-emerald-800 text-sm font-bold border border-emerald-200"
                        data-testid="mark-present-btn">
                  <CheckCircle2 className="w-4 h-4" />
                  {savingPresent ? "Marking…" : "Mark Present"}
                </button>
                <button onClick={openLogLeftEarly}
                        className="btn-touch inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-100 hover:bg-orange-200 text-orange-800 text-sm font-bold border border-orange-200"
                        data-testid="log-left-early-btn">
                  <LogOutIcon className="w-4 h-4" />
                  Left Early
                </button>
                <button onClick={openLogArriveLate}
                        className="btn-touch inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-bold border border-amber-200"
                        data-testid="log-arrive-late-btn">
                  <Clock className="w-4 h-4" />
                  Arrive Late
                </button>
                {data.late_arrival_role && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-200 px-2 py-1 rounded" data-testid="late-arrival-badge">
                    Late-Arrival Role
                  </span>
                )}
              </div>

              {/* Inline popover for Left Early */}
              {leOpen && (
                <div className="rounded-xl border-2 border-orange-300 bg-orange-50 p-3 space-y-2" data-testid="log-left-early-form">
                  <div className="text-xs font-bold uppercase tracking-[0.14em] text-orange-800 flex items-center gap-1.5">
                    <LogOutIcon className="w-3.5 h-3.5" /> Log Left Early — Today
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs">
                      <div className="text-slate-600 font-semibold mb-1">Time left (24h)</div>
                      <input
                        type="time"
                        value={leTime}
                        onChange={(e) => setLeTime(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono"
                        data-testid="log-left-early-time"
                      />
                    </label>
                    <label className="text-xs">
                      <div className="text-slate-600 font-semibold mb-1">Reason (optional)</div>
                      <input
                        type="text"
                        value={leReason}
                        onChange={(e) => setLeReasonInput(e.target.value)}
                        placeholder="e.g. Doctor appointment"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                        data-testid="log-left-early-reason"
                      />
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={submitLogLeftEarly}
                      disabled={savingLe || !leTime}
                      className="btn-touch inline-flex items-center gap-1.5 px-3 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold text-sm"
                      data-testid="log-left-early-submit"
                    >
                      {savingLe ? "Saving…" : "Save Left Early"}
                    </button>
                    <button
                      onClick={() => setLeOpen(false)}
                      className="btn-touch px-3 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-sm"
                      data-testid="log-left-early-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Records today&apos;s sign-out at this time and flags the day as <strong>Left Early</strong> for reports.
                  </div>
                </div>
              )}

              {/* Inline popover for Arrive Late */}
              {alOpen && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="log-arrive-late-form">
                  <div className="text-xs font-bold uppercase tracking-[0.14em] text-amber-800 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Log Arrive Late — Today
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="text-xs">
                      <div className="text-slate-600 font-semibold mb-1">Time arrived (24h)</div>
                      <input
                        type="time"
                        value={alTime}
                        onChange={(e) => setAlTime(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono"
                        data-testid="log-arrive-late-time"
                      />
                    </label>
                    <label className="text-xs">
                      <div className="text-slate-600 font-semibold mb-1">Reason (optional)</div>
                      <input
                        type="text"
                        value={alReason}
                        onChange={(e) => setAlReasonInput(e.target.value)}
                        placeholder="e.g. Traffic on Rt. 22"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                        data-testid="log-arrive-late-reason"
                      />
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={submitLogArriveLate}
                      disabled={savingAl || !alTime}
                      className="btn-touch inline-flex items-center gap-1.5 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold text-sm"
                      data-testid="log-arrive-late-submit"
                    >
                      {savingAl ? "Saving…" : "Save Arrive Late"}
                    </button>
                    <button
                      onClick={() => setAlOpen(false)}
                      className="btn-touch px-3 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-sm"
                      data-testid="log-arrive-late-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Records today&apos;s sign-in at this time. Status (Present / Late / Late Arrival) is set from configured thresholds.
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { label: "Sign-ins", value: data.stats?.total_signins, color: "text-slate-900" },
                { label: "Present", value: data.stats?.present, color: "text-emerald-700" },
                { label: "Late", value: data.stats?.late, color: "text-amber-700" },
                { label: "Left Early", value: data.stats?.early_departures, color: "text-orange-700" },
                { label: "Planned Off", value: data.stats?.planned_absences, color: "text-violet-700" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                  <div className={`font-display text-2xl font-bold tabular-nums ${s.color}`}>{s.value ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Termination banner — READ-ONLY (2026-07-05).
                Terminated staff stay visible for payroll; the banner shows
                status/date/reason but has NO controls to change termination
                state. This is by explicit admin instruction. */}
            {data.is_terminated && (
              <div
                className="rounded-xl border-2 border-red-300 bg-red-50 p-3"
                data-testid="terminated-banner"
              >
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-red-700 flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  Terminated
                  {data.terminated_at_utc && (
                    <span className="text-red-600 font-mono normal-case tracking-normal">
                      · {new Date(data.terminated_at_utc).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="text-sm text-red-900 mt-1">
                  <span className="font-semibold">Reason:</span>{" "}
                  <span className="capitalize">
                    {(data.terminated_reason || "unknown").replace(/_/g, " ")}
                  </span>
                </div>
                <div className="text-[11px] text-red-700 mt-0.5">
                  History preserved. Login blocked. Payroll can still see this record.
                </div>
              </div>
            )}

            {/* TAB BAR */}
            <div className="flex gap-1 border-b border-slate-200 -mx-4 sm:-mx-6 px-4 sm:px-6 sticky top-0 bg-white z-10 overflow-x-auto no-scrollbar" data-testid="card-tabs">
              {[
                { key: "overview", label: "Overview", count: null },
                { key: "absences", label: "Planned Absences", count: data.absences?.length || 0 },
                { key: "latenesses", label: "Planned Lateness", count: data.latenesses?.length || 0 },
                { key: "notes", label: "Notes", count: data.notes?.length || 0 },
                { key: "history", label: "Sign-in History", count: data.signin_history?.length || 0 },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] border-b-2 -mb-px transition whitespace-nowrap ${
                    activeTab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`card-tab-${t.key}`}
                >
                  {t.label}{t.count !== null && <span className="ml-1.5 text-slate-400 normal-case font-mono">({t.count})</span>}
                </button>
              ))}
            </div>

            {/* OVERVIEW TAB — Leave-Early form lives here */}
            {activeTab === "overview" && (
            <>
            {/* PAYROLL — leadership only (renders nothing for regular admins) */}
            <PayrollPanel crewId={crewId} crewName={data.member?.name} />
            {/* LEAVE EARLY — today only. Hidden for terminated staff — they
                cannot sign in today, so leave-early doesn't apply. */}
            {!data.is_terminated && (
            <div className="rounded-2xl border border-slate-200 bg-orange-50/50 p-4" data-testid="leave-early-section">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <DoorOpen className="w-4 h-4 text-orange-600" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Leave Early — Today</h3>
                </div>
                {!todayLeaveEarly && !leaveOpen && (
                  <button onClick={() => setLeaveOpen(true)} className="btn-touch px-3 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs" data-testid="open-leave-early">
                    Request leave-early →
                  </button>
                )}
              </div>
              {todayLeaveEarly ? (
                <div className={`p-3 rounded-lg border ${todayLeaveEarly.decision === "approved" ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}>
                  <div className="flex items-center gap-2 font-bold text-sm mb-2">
                    {todayLeaveEarly.decision === "approved"
                      ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="text-emerald-800">Approved</span></>
                      : <><XCircle className="w-4 h-4 text-red-600" /><span className="text-red-800">Denied</span></>}
                  </div>
                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs mb-2">
                    <div><dt className="text-slate-500 uppercase tracking-[0.12em] font-bold">Name</dt><dd className="text-slate-900 font-semibold">{data.name || "—"}</dd></div>
                    <div><dt className="text-slate-500 uppercase tracking-[0.12em] font-bold">Date</dt><dd className="text-slate-900 font-mono">{todayLeaveEarly.date}</dd></div>
                    <div><dt className="text-slate-500 uppercase tracking-[0.12em] font-bold">Position</dt><dd className="text-slate-900 truncate">{data.position || "—"}</dd></div>
                    <div><dt className="text-slate-500 uppercase tracking-[0.12em] font-bold">Division</dt><dd className="text-slate-900 truncate">{data.division || "—"}</dd></div>
                  </dl>
                  <div className="text-sm text-slate-800 mb-2"><strong>Reason:</strong> {todayLeaveEarly.reason}</div>
                  <div className="text-xs text-slate-500 italic" data-testid="leave-early-signature">Signed by <strong className="text-slate-700">{todayLeaveEarly.approved_by_label}</strong> · {todayLeaveEarly.decided_at_local_display}</div>
                </div>
              ) : leaveOpen ? (
                <div className="space-y-3" data-testid="leave-early-form">
                  {/* Auto-populated read-only fields */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-lg bg-white border border-slate-200">
                    <Field label="Staff Name" value={data.name || "—"} testId="le-name" />
                    <Field label="Date" value={new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} testId="le-date" />
                    <Field label="Time" value={new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} testId="le-time" />
                    <Field label="Position" value={data.position || "—"} testId="le-position" />
                    <Field label="Division" value={data.division || "—"} testId="le-division" full />
                  </div>
                  <textarea
                    rows={3}
                    value={leaveReason}
                    onChange={(e) => setLeaveReason(e.target.value)}
                    placeholder="Reason for leaving early — required."
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                    data-testid="leave-early-reason"
                  />
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-1.5">Supervisor signature *</div>
                    <select
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                      data-testid="leave-early-signer"
                    >
                      <option value="">— Select supervisor name —</option>
                      {supervisors.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <div className="text-xs text-slate-500 mt-1.5">Your name will be saved as the signer on the card and the day's report.</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button disabled={submittingLeave || !leaveReason.trim() || !signerName} onClick={() => submitLeaveEarly("approved")} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-40" data-testid="leave-early-approve">
                      <CheckCircle2 className="w-4 h-4" /> Approve & Sign
                    </button>
                    <button disabled={submittingLeave || !leaveReason.trim() || !signerName} onClick={() => submitLeaveEarly("denied")} className="btn-touch flex-1 inline-flex items-center justify-center gap-1 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm disabled:opacity-40" data-testid="leave-early-deny">
                      <XCircle className="w-4 h-4" /> Deny & Sign
                    </button>
                    <button onClick={() => { setLeaveOpen(false); setLeaveReason(""); setSignerName(""); }} className="btn-touch px-3 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-sm" data-testid="leave-early-cancel">Cancel</button>
                  </div>
                  <div className="text-xs text-slate-500">Approval lets the staff member sign out before 3:45 PM as <strong>Left Early</strong> with this reason on the day's report.</div>
                </div>
              ) : (
                <div className="text-sm text-slate-500 italic">No leave-early request today.</div>
              )}
            </div>
            )}

            {/* NOTES — shared, dated, timestamped */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="notes-section">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquarePlus className="w-4 h-4 text-blue-600" />
                <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Recent Notes ({data.notes?.length || 0})</h3>
              </div>
              {(!data.notes || data.notes.length === 0) ? (
                <div className="text-sm text-slate-500 italic">No notes yet — switch to the Notes tab to add one.</div>
              ) : (
                <ul className="space-y-1.5" data-testid="recent-notes-preview">
                  {data.notes.slice(0, 3).map((n) => (
                    <li key={n.id} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs">
                      <div className="text-[10px] text-slate-500 font-mono mb-0.5"><strong className="text-slate-700">{n.author_label}</strong> · {n.created_at_local?.slice(0, 16).replace("T", " ")}</div>
                      <div className="text-slate-800 whitespace-pre-wrap line-clamp-2">{n.text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            </>
            )}

            {/* PLANNED ABSENCES TAB */}
            {activeTab === "absences" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="absences-tab">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-violet-600" />
                    <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Planned Absences ({data.absences?.length || 0})</h3>
                  </div>
                  <button onClick={() => setPaOpen((v) => !v)} disabled={data.is_terminated} className="btn-touch px-3 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs inline-flex items-center gap-1" data-testid="open-add-planned-absence" title={data.is_terminated ? "Terminated staff cannot have new absences" : ""}>
                    <span className="text-base leading-none">+</span> Add absence
                  </button>
                </div>
                {paOpen && (
                  <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 mb-3 space-y-2" data-testid="planned-absence-form">
                    {/* Mode toggle: Single date vs Range */}
                    <div className="inline-flex rounded-lg overflow-hidden border border-violet-200 text-xs font-bold" data-testid="pa-mode-toggle">
                      <button type="button" onClick={() => setPaMode("single")} className={`px-3 py-1.5 ${paMode === "single" ? "bg-violet-600 text-white" : "bg-white text-slate-600 hover:bg-violet-50"}`} data-testid="pa-mode-single">Single day</button>
                      <button type="button" onClick={() => setPaMode("range")} className={`px-3 py-1.5 ${paMode === "range" ? "bg-violet-600 text-white" : "bg-white text-slate-600 hover:bg-violet-50"}`} data-testid="pa-mode-range">Date range (vacation)</button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">{paMode === "range" ? "Start date" : "Date"}</span>
                        <input type="date" value={paDate} onChange={(e) => setPaDate(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pa-date" />
                      </label>
                      {paMode === "range" ? (
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">End date <span className="text-slate-400 normal-case">(inclusive)</span></span>
                          <input type="date" value={paEndDate} min={paDate || undefined} onChange={(e) => setPaEndDate(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pa-end-date" />
                        </label>
                      ) : (
                        <label className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Label <span className="text-slate-400 normal-case">(optional)</span></span>
                          <input type="text" value={paLabel} onChange={(e) => setPaLabel(e.target.value)} placeholder="e.g. Orientation 1" className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pa-label" />
                        </label>
                      )}
                    </div>
                    {paMode === "range" && (
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Label <span className="text-slate-400 normal-case">(applied to each day, optional)</span></span>
                        <input type="text" value={paLabel} onChange={(e) => setPaLabel(e.target.value)} placeholder="e.g. Family vacation" className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pa-label-range" />
                      </label>
                    )}
                    <textarea
                      rows={2}
                      value={paReason}
                      onChange={(e) => setPaReason(e.target.value)}
                      placeholder={paMode === "range" ? "Reason (applied to each day, optional)" : "Reason (optional)"}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-violet-400 text-sm bg-white"
                      data-testid="pa-reason"
                    />
                    {paMode === "range" && paDate && paEndDate && paEndDate >= paDate && (
                      <div className="text-xs text-violet-700 font-semibold" data-testid="pa-range-preview">
                        Will create {Math.round((new Date(paEndDate) - new Date(paDate)) / 86400000) + 1} planned absence{Math.round((new Date(paEndDate) - new Date(paDate)) / 86400000) + 1 !== 1 ? "s" : ""} ({paDate} → {paEndDate}, inclusive).
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <button disabled={savingPa || !paDate || (paMode === "range" && !paEndDate)} onClick={addPlannedAbsence} className="btn-touch px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs disabled:opacity-40" data-testid="pa-save">
                        {savingPa ? "Saving…" : (paMode === "range" ? "Save range" : "Save absence")}
                      </button>
                      <button onClick={() => { setPaOpen(false); setPaMode("single"); setPaDate(""); setPaEndDate(""); setPaReason(""); setPaLabel(""); }} className="btn-touch px-3 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-xs" data-testid="pa-cancel">Cancel</button>
                    </div>
                  </div>
                )}
                {(!data.absences || data.absences.length === 0) ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <div className="font-semibold text-sm">No planned absences on record</div>
                    <div className="text-xs mt-1">Click <strong>+ Add absence</strong> above, or staff can submit via the Sign-In portal.</div>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="planned-absence-list">
                    {data.absences.map((a) => (
                      <div key={a.date} className={`flex items-start gap-2 sm:gap-3 p-3 rounded-lg border ${a.source === "admin_mark_absent" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid={`absence-${a.date}`}>
                        <Calendar className={`w-4 h-4 mt-0.5 shrink-0 ${a.source === "admin_mark_absent" ? "text-red-700" : "text-amber-700"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 text-sm break-words">{fmtPretty(a.date)}</div>
                          {a.label && <div className="text-sm font-bold text-slate-800 mt-0.5">{a.label}</div>}
                          {a.reason ? (
                            <div className="text-sm text-slate-700 mt-0.5">{a.reason}</div>
                          ) : a.source === "campminder" ? (
                            <div className="text-xs text-slate-400 italic mt-0.5">No reason provided in CampMinder</div>
                          ) : null}
                          {(a.added_by || a.added_at_utc) && (
                            <div className="text-[10px] text-slate-500 mt-1 font-mono">
                              {a.added_by ? `by ${a.added_by}` : null}
                              {a.added_at_utc ? ` · ${a.added_at_utc.slice(0, 16).replace("T", " ")}` : null}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <div className="flex flex-wrap items-center gap-1 justify-end">
                            {a.category && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] border ${a.category === "sick" ? "bg-red-100 text-red-800 border-red-200" : "bg-white text-slate-600 border-slate-200"}`}>{a.category}</span>
                            )}
                            {a.source === "campminder" && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] bg-amber-100 text-amber-900 border border-amber-200" title="Synced from CampMinder custom fields. Delete in Staff Snapshot to suppress on next sync.">CampMinder</span>
                            )}
                          </div>
                          <button onClick={() => removePlannedAbsence(a.date)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Remove" data-testid={`pa-remove-${a.date}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PLANNED LATENESS TAB */}
            {activeTab === "latenesses" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="latenesses-tab">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Planned Lateness ({data.latenesses?.length || 0})</h3>
                  </div>
                  <button onClick={() => setPlOpen((v) => !v)} disabled={data.is_terminated} className="btn-touch px-3 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs inline-flex items-center gap-1" data-testid="open-add-planned-lateness" title={data.is_terminated ? "Terminated staff cannot have new latenesses" : ""}>
                    <span className="text-base leading-none">+</span> Add lateness
                  </button>
                </div>
                {plOpen && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 mb-3 space-y-2" data-testid="planned-lateness-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Date</span>
                        <input type="date" value={plDate} onChange={(e) => setPlDate(e.target.value)} className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pl-date" />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Label <span className="text-slate-400 normal-case">(optional)</span></span>
                        <input type="text" value={plLabel} onChange={(e) => setPlLabel(e.target.value)} placeholder="e.g. Doctor appt" className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm bg-white" data-testid="pl-label" />
                      </label>
                    </div>
                    <textarea
                      rows={2}
                      value={plReason}
                      onChange={(e) => setPlReason(e.target.value)}
                      placeholder="Reason (optional)"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-white"
                      data-testid="pl-reason"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={addPlannedLateness} className={`btn-touch px-3 rounded-lg font-bold text-xs text-white ${savingPl ? "bg-amber-400" : "bg-amber-600 hover:bg-amber-700"}`} data-testid="pl-save">
                        {savingPl ? "Saving…" : "Save lateness"}
                      </button>
                      <button onClick={() => { setPlOpen(false); setPlDate(""); setPlReason(""); setPlLabel(""); }} className="btn-touch px-3 rounded-lg bg-white border border-slate-200 text-slate-700 font-semibold text-xs" data-testid="pl-cancel">Cancel</button>
                    </div>
                  </div>
                )}
                {(!data.latenesses || data.latenesses.length === 0) ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <div className="font-semibold text-sm">No planned latenesses on record</div>
                    <div className="text-xs mt-1">Click <strong>+ Add lateness</strong> above to schedule a late arrival.</div>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="planned-lateness-list">
                    {data.latenesses.map((L) => (
                      <div key={L.date} className="flex items-start gap-2 sm:gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid={`lateness-${L.date}`}>
                        <Clock className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 text-sm break-words">
                            {fmtPretty(L.date)}
                          </div>
                          {L.label && <div className="text-sm font-bold text-slate-800 mt-0.5">{L.label}</div>}
                          {L.reason && <div className="text-sm text-slate-700 mt-0.5">{L.reason}</div>}
                          {(L.added_by || L.added_at_utc) && (
                            <div className="text-[10px] text-slate-500 mt-1 font-mono">
                              {L.added_by ? `by ${L.added_by}` : null}
                              {L.added_at_utc ? ` · ${L.added_at_utc.slice(0, 16).replace("T", " ")}` : null}
                            </div>
                          )}
                        </div>
                        <button onClick={() => removePlannedLateness(L.date)} className="p-1.5 rounded hover:bg-red-50 text-red-500 shrink-0" title="Remove" data-testid={`pl-remove-${L.date}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* NOTES TAB — full editor */}
            {activeTab === "notes" && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="notes-tab">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquarePlus className="w-4 h-4 text-blue-600" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Notes ({data.notes?.length || 0})</h3>
                </div>
                <div className="space-y-2 mb-3">
                  <textarea
                    rows={3}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a shared note (visible to all who can view this card)…"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                    data-testid="note-input"
                  />
                  <button onClick={addNote} disabled={savingNote || !noteText.trim()} className="btn-touch px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs disabled:opacity-40" data-testid="note-save">
                    {savingNote ? "Saving…" : "Save Note"}
                  </button>
                </div>
                {(!data.notes || data.notes.length === 0) ? (
                  <div className="text-sm text-slate-500 italic">No notes yet.</div>
                ) : (
                  <ul className="space-y-2" data-testid="note-list">
                    {data.notes.map((n) => (
                      <li key={n.id} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid={`note-${n.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="text-[11px] text-slate-500 font-mono">
                            <strong className="text-slate-700">{n.author_label}</strong> · {n.created_at_local?.slice(0, 16).replace("T", " ")}
                          </div>
                          {isAdmin && (
                            <button onClick={() => deleteNote(n.id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Delete note" data-testid={`note-delete-${n.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === "history" && (
              <div data-testid="history-tab">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h3 className="font-display font-bold text-sm uppercase tracking-[0.14em] text-slate-700">Sign-in History ({data.signin_history?.length || 0})</h3>
                </div>
                {(!data.signin_history || data.signin_history.length === 0) ? (
                  <div className="text-sm text-slate-500 italic px-3 py-2">No sign-in history yet.</div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm" data-testid="signin-history-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Date</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Segments</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Worked</th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.signin_history.map((s) => {
                          const segs = (s.segments && s.segments.length) ? s.segments : [{
                            sign_in_local_display: s.sign_in_local_display,
                            sign_out_local_display: s.sign_out_local_display,
                            sign_out_method: s.sign_out_method,
                            open: !s.sign_out_local_display,
                          }];
                          const totalSec = s.worked_seconds_total ?? 0;
                          const activeSec = s.worked_seconds_active ?? 0;
                          const fmtH = (sec) => {
                            const h = Math.floor(sec / 3600);
                            const m = Math.round((sec % 3600) / 60);
                            return h ? `${h}h ${m}m` : `${m}m`;
                          };
                          return (
                          <tr key={s.date + s.crew_id} className="border-t border-slate-100" data-testid={`history-row-${s.date}`}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-700 align-top">{s.date}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex flex-col gap-0.5">
                                {segs.map((seg, i) => (
                                  <span key={i} className="font-mono text-xs tabular-nums text-slate-700" data-testid={`history-seg-${s.date}-${i}`}>
                                    {seg.sign_in_local_display || "—"}
                                    <span className="text-slate-400 mx-1">→</span>
                                    {seg.open || !seg.sign_out_local_display ? (
                                      <span className="text-emerald-700 font-bold">now</span>
                                    ) : (
                                      <>
                                        {seg.sign_out_local_display}
                                        {seg.sign_out_method === "auto_eod" || s.auto_signed_out ? (
                                          <span className="ml-1 text-[9px] text-slate-400 uppercase">auto</span>
                                        ) : null}
                                      </>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top text-right">
                              <div className="font-mono text-sm font-bold text-slate-900 tabular-nums">{fmtH(totalSec)}</div>
                              {activeSec > 0 && (
                                <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-[0.1em]">+{fmtH(activeSec)} active</div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] ${STATUS_PILL[s.status] || "bg-slate-100 text-slate-600"}`}>{s.status}</span>
                              {s.early_departure && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.1em] bg-orange-100 text-orange-800">Left Early</span>}
                            </td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500">Profile unavailable.</div>
        )}

        {/* Mark Absent modal */}
        {maOpen && (
          <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setMaOpen(false)} data-testid="mark-absent-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-red-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="font-display font-bold">Out sick — illness</div>
                <button onClick={() => setMaOpen(false)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">When?</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => { setMaMode("today"); const t = todayISO(); setMaStart(t); setMaEnd(t); }}
                      className={`px-3 py-3 rounded-xl border-2 font-bold transition ${maMode === "today" ? "border-red-500 bg-red-50 text-red-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                      data-testid="mark-absent-today-btn">
                      Today only
                    </button>
                    <button
                      onClick={() => setMaMode("range")}
                      className={`px-3 py-3 rounded-xl border-2 font-bold transition ${maMode === "range" ? "border-red-500 bg-red-50 text-red-800" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                      data-testid="mark-absent-range-btn">
                      Date range
                    </button>
                  </div>
                </div>

                {maMode === "range" && (
                  <div className="grid grid-cols-2 gap-2" data-testid="mark-absent-range-fields">
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Start</span>
                      <input type="date" value={maStart} onChange={(e) => setMaStart(e.target.value)}
                             className="px-3 py-2 rounded-lg border border-slate-200" data-testid="mark-absent-start" />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">End</span>
                      <input type="date" value={maEnd} onChange={(e) => setMaEnd(e.target.value)}
                             className="px-3 py-2 rounded-lg border border-slate-200" data-testid="mark-absent-end" />
                    </label>
                  </div>
                )}

                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Header</span>
                  <input type="text" value={maLabel} onChange={(e) => setMaLabel(e.target.value)}
                         maxLength={80}
                         className="px-3 py-2 rounded-lg border border-slate-200" data-testid="mark-absent-label" />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">Reason / notes (optional)</span>
                  <textarea rows={2} value={maReason} onChange={(e) => setMaReason(e.target.value)}
                            maxLength={500} placeholder="e.g. Called out — flu, expects to be back Monday"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                            data-testid="mark-absent-reason" />
                </label>

                <div className="flex gap-2 pt-1">
                  <button onClick={() => setMaOpen(false)}
                          className="btn-touch flex-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold">
                    Cancel
                  </button>
                  <button onClick={submitMarkAbsent} disabled={savingMa}
                          className="btn-touch flex-1 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50"
                          data-testid="mark-absent-submit">
                    {savingMa ? "Saving…" : "Mark Absent"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mark Present modal — date selector for back-fills */}
        {mpOpen && (
          <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => !savingPresent && setMpOpen(false)} data-testid="mark-present-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-3.5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-bold">Manual Override</div>
                <div className="font-display text-lg font-bold">Mark Present</div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Pick the day they were here. Use this when the kiosk
                  failed or you forgot to sign someone in.
                </p>
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Date</span>
                  <input
                    type="date"
                    value={mpDate}
                    onChange={(e) => setMpDate(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-400 outline-none text-sm tabular-nums"
                    data-testid="mark-present-date"
                  />
                </label>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setMpOpen(false)}
                    disabled={savingPresent}
                    className="btn-touch flex-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold"
                    data-testid="mark-present-cancel"
                  >Cancel</button>
                  <button
                    onClick={submitMarkPresent}
                    disabled={savingPresent || !mpDate}
                    className="btn-touch flex-1 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
                    data-testid="mark-present-submit"
                  >{savingPresent ? "Saving…" : "Mark Present"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StaffCardTile({ m, onOpen }) {
  const isTerm = !!m.is_terminated;
  return (
    <button
      onClick={() => onOpen(m.crew_id)}
      className={`text-left rounded-xl border transition p-3 bg-white ${
        isTerm
          ? "border-slate-300 hover:border-slate-400 opacity-80"
          : "border-slate-200 hover:border-blue-400 hover:shadow-md"
      }`}
      data-testid={`staff-card-${m.crew_id}`}
      data-terminated={isTerm ? "true" : "false"}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          {m.photo_thumb_base64 ? (
            <img
              src={`data:image/jpeg;base64,${m.photo_thumb_base64}`}
              alt=""
              className={`w-11 h-11 rounded-full object-cover border border-slate-200 shrink-0 ${
                isTerm ? "grayscale" : ""
              }`}
              data-testid={`staff-avatar-${m.crew_id}`}
            />
          ) : (
            <div
              className="w-11 h-11 rounded-full bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-400 font-bold text-xs"
              aria-hidden="true"
            >
              {(m.name || "?")
                .split(" ")
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate">{formatLastFirst(m.name) || "Unknown"}</div>
            {m.position && <div className="text-xs text-slate-500 truncate">{m.position}</div>}
            {isTerm && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                <span
                  className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] bg-red-100 text-red-800"
                  data-testid={`terminated-badge-${m.crew_id}`}
                >
                  Terminated
                </span>
                {m.terminated_at_utc && (
                  <span className="text-[10px] font-mono text-slate-500">
                    {new Date(m.terminated_at_utc).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
            {!isTerm && m.today?.status && m.today.status !== "—" && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] ${STATUS_PILL[m.today.status] || "bg-slate-100 text-slate-600"}`} data-testid={`today-status-${m.crew_id}`}>{m.today.status}</span>
                {m.today.sign_in && <span className="text-[10px] font-mono text-slate-500">{m.today.sign_in}{m.today.sign_out ? ` → ${m.today.sign_out}` : ""}</span>}
                {m.today.early_departure && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] bg-orange-100 text-orange-800">Left Early</span>}
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.14em]">Absences</div>
          <div className="font-display text-lg font-bold text-violet-700 tabular-nums" data-testid={`absence-count-${m.crew_id}`}>{m.stats?.planned_absences ?? 0}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
        <span className="tabular-nums font-mono">{m.phone || "—"}</span>
        <span className="text-slate-300">•</span>
        <span>Sign-ins: <span className="font-semibold text-slate-700 tabular-nums">{m.stats?.total_signins ?? 0}</span></span>
        {(m.stats?.early_departures || 0) > 0 && (
          <>
            <span className="text-slate-300">•</span>
            <span className="text-orange-700 font-semibold">{m.stats.early_departures} early</span>
          </>
        )}
        {(m.stats?.planned_absences || 0) > 0 && (
          <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] bg-violet-100 text-violet-800" data-testid={`planned-badge-${m.crew_id}`} title="Click card to view dates">
            {m.stats.planned_absences} planned off
          </span>
        )}
        {(m.stats?.planned_latenesses || 0) > 0 && (
          <span
            className={`${(m.stats?.planned_absences || 0) > 0 ? "" : "ml-auto"} px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.1em] bg-amber-100 text-amber-900`}
            data-testid={`lateness-badge-${m.crew_id}`}
            title="Planned late arrivals — click card to view"
          >
            {m.stats.planned_latenesses} L
          </span>
        )}
      </div>
    </button>
  );
}

function BunkAccordion({ divisionName, bunk, onOpen, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  const isUnassigned = bunk.key === "__unassigned__";
  const safeId = `${divisionName}-${bunk.label}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div
      className={`rounded-xl border ${open ? "border-blue-300 shadow-sm" : "border-slate-200"} bg-white overflow-hidden transition`}
      data-testid={`bunk-card-${safeId}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition"
        data-testid={`bunk-toggle-${safeId}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open
            ? <ChevronDown className="w-4 h-4 text-blue-600 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
          <Home className={`w-4 h-4 shrink-0 ${isUnassigned ? "text-slate-400" : "text-blue-600"}`} />
          <span className={`font-display font-bold text-sm sm:text-base truncate ${isUnassigned ? "text-slate-500 italic" : "text-slate-900"}`}>
            {bunk.label}
          </span>
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 shrink-0" data-testid={`bunk-count-${safeId}`}>
          {bunk.members.length} staff
        </span>
      </button>
      {open && (
        <div className="px-3 sm:px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/40">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            {bunk.members.map((m) => (
              <StaffCardTile key={m.crew_id} m={m} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



export default function StaffCards() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [terminatedGroups, setTerminatedGroups] = useState([]);
  const [terminatedTotal, setTerminatedTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState("admin");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [leaveEarlyToday, setLeaveEarlyToday] = useState([]);

  // Add Custom Staff (admin only) — bypasses CampMinder for off-roster hires
  // like security guards / one-off contractors.
  const isAdmin = getLevel() === "admin";
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addDivision, setAddDivision] = useState("Office");
  const [addPosition, setAddPosition] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addYears, setAddYears] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const DIVISION_OPTIONS = [
    "Office", "Pool", "Athletics", "Boating", "Mariners", "Skippers",
    "Navigators", "Dining", "Specialty", "Nurses", "Maintenance", "Supervisors",
  ];
  const submitAddCrew = async () => {
    if (addSaving) return;
    const name = addName.trim();
    const division = addDivision.trim();
    if (name.length < 2 || division.length < 2) {
      toast.error("Name + division are required");
      return;
    }
    setAddSaving(true);
    try {
      const body = { name, division };
      if (addPosition.trim()) body.position = addPosition.trim();
      if (addPhone.trim()) body.phone = addPhone.trim();
      if (addEmail.trim()) body.email = addEmail.trim();
      if (addYears !== "" && !Number.isNaN(parseInt(addYears, 10))) {
        body.years_at_camp = parseInt(addYears, 10);
      }
      const r = await api.post("/admin/crew", body);
      toast.success(`Added ${r.data?.name || name}`);
      setAddOpen(false);
      setAddName(""); setAddPosition(""); setAddPhone(""); setAddEmail(""); setAddYears("");
      setAddDivision("Office");
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add staff");
    } finally { setAddSaving(false); }
  };

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  const [rosterError, setRosterError] = useState(null);
  const [rosterDiagnostic, setRosterDiagnostic] = useState(null);

  const load = async (q) => {
    setLoading(true);
    try {
      const res = await api.get("/staff-cards", { params: q ? { search: q } : {} });
      setGroups(res.data.groups || []);
      setTerminatedGroups(res.data.terminated_groups || []);
      setTerminatedTotal(res.data.terminated_total || 0);
      setTotal(res.data.total || 0);
      setScope(res.data.scope || "admin");
      // Structural-fix (2026-07-04): the API now returns `_error` +
      // `_diagnostic` when the roster is partial or degraded, instead of
      // silently collapsing to an empty list. Surface both so admins see
      // WHY the roster is short rather than a misleading "empty" screen.
      setRosterError(res.data._error || null);
      setRosterDiagnostic(res.data._diagnostic || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load staff cards");
      setRosterError("network_error");
      setRosterDiagnostic(null);
    } finally { setLoading(false); }
  };

  const loadBanner = async () => {
    try {
      const res = await api.get("/leave-early/today");
      setLeaveEarlyToday(res.data.items || []);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { load(""); loadBanner(); }, []);
  useEffect(() => {
    const t = setTimeout(() => { load(search); }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const visibleGroups = useMemo(() => groups.filter((g) => g.count > 0), [groups]);
  const logout = () => { clearSession(); navigate("/"); };
  const approvedToday = leaveEarlyToday.filter((x) => x.decision === "approved");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold text-sm" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River — {scope === "admin" ? "All Divisions" : `${sessionStorage.getItem("rrdc_division_name") || scope} Division`}</div>
            <h1 className="font-display text-lg sm:text-xl font-bold text-slate-900">Staff Cards <span className="text-slate-400 font-normal text-sm tabular-nums">({total})</span></h1>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-1 text-slate-500 hover:text-red-700 text-sm font-semibold" data-testid="staff-cards-logout">
            <LogOutIcon className="w-4 h-4" /> Log out
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone or division…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                data-testid="staff-cards-search"
              />
            </div>
            {isAdmin && (
              <button
                onClick={() => setAddOpen(true)}
                className="btn-touch inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shrink-0"
                data-testid="add-custom-staff-btn"
                title="Add a staff member not in CampMinder (security, contractors, etc.)"
              >
                + Add Staff
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 space-y-6">
        {approvedToday.length > 0 && (
          <div className="rounded-2xl border border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 p-4 sm:p-5" data-testid="left-early-banner">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-orange-600 text-white"><svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 16h6M12 13v3M3 12a9 9 0 1118 0 9 9 0 01-18 0z"/></svg></span>
              <h2 className="font-display font-bold text-base sm:text-lg text-slate-900">Today's Early Departures <span className="text-slate-400 font-normal">({approvedToday.length})</span></h2>
            </div>
            <ul className="space-y-1.5" data-testid="left-early-banner-list">
              {approvedToday.map((it) => (
                <li key={it.crew_id + it.date} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm rounded-lg bg-white border border-orange-200 px-3 py-2" data-testid={`banner-row-${it.crew_id}`}>
                  <span className="font-bold text-slate-900">{formatLastFirst(it.name) || "Unknown"}</span>
                  {it.division && <span className="text-xs text-slate-500 font-mono">{it.division}</span>}
                  <span className="text-orange-700 font-semibold text-xs">left at {it.decided_at_local_display}</span>
                  <span className="flex-1 min-w-0 text-slate-700 italic truncate">"{it.reason}"</span>
                  <span className="text-[10px] text-slate-400 uppercase tracking-[0.14em]">signed by {it.approved_by_label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {loading ? (
          <div className="text-center py-10 text-slate-500">Loading staff…</div>
        ) : rosterError && rosterError !== "network_error" ? (
          <div
            className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 sm:p-8"
            data-testid="roster-error-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-display font-bold text-amber-900 text-lg">
                  {rosterError === "roster_partial"
                    ? "Roster is incomplete"
                    : rosterError === "roster_load_failed"
                    ? "Couldn't load roster"
                    : "Roster loaded with issues"}
                </div>
                <div className="text-sm text-amber-800 mt-1">
                  {rosterError === "roster_partial" && rosterDiagnostic
                    ? `Only ${rosterDiagnostic.returned_card_count || 0} of ${rosterDiagnostic.expected_card_count || 0} staff cards built. The rest were lost mid-pipeline.`
                    : rosterError === "roster_load_failed"
                    ? "The roster query failed. Tap Refresh below or try again in a moment."
                    : "Cards rendered but some data (sign-ins / absences / lateness) could not be loaded."}
                </div>
                {rosterDiagnostic?.errors?.length > 0 && (
                  <details className="mt-3 text-xs text-amber-900" data-testid="roster-diagnostic-details">
                    <summary className="cursor-pointer font-semibold">
                      Show technical details ({rosterDiagnostic.errors.length} failing step{rosterDiagnostic.errors.length === 1 ? "" : "s"})
                    </summary>
                    <ul className="mt-2 space-y-1.5 font-mono">
                      {rosterDiagnostic.errors.map((err, i) => (
                        <li key={i} className="border-l-2 border-amber-400 pl-2">
                          <span className="font-bold">{err.step}</span>: {err.type} — {err.message}
                        </li>
                      ))}
                    </ul>
                    {rosterDiagnostic.active_crew_count_in_db != null && (
                      <div className="mt-2">
                        <span className="font-semibold">DB active crew:</span> {rosterDiagnostic.active_crew_count_in_db} · <span className="font-semibold">Loaded:</span> {rosterDiagnostic.expected_card_count} · <span className="font-semibold">Built:</span> {rosterDiagnostic.returned_card_count}
                      </div>
                    )}
                  </details>
                )}
                <button
                  onClick={() => load(search)}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
                  data-testid="roster-error-refresh"
                >
                  <RotateCcw className="w-4 h-4" /> Refresh
                </button>
              </div>
            </div>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="roster-empty-state">
            <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <div className="font-semibold">No staff found{search ? ` for “${search}”` : ""}.</div>
            <div className="text-sm mt-1">
              {search
                ? "Try a shorter search or clear the filter."
                : "There are no active staff in the roster yet. Once CampMinder syncs your Hired list, they'll appear here."}
            </div>
          </div>
        ) : (
          visibleGroups.map((g) => {
            const groupDivision = isGroupDivision(g.division);
            const bunks = groupDivision ? groupMembersByBunk(g.members) : null;
            return (
            <section key={g.division} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={`group-${g.division}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold text-base sm:text-lg text-slate-900">{g.division}</h2>
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {g.count} staff{groupDivision && bunks ? ` · ${bunks.filter((b) => b.key !== "__unassigned__").length} bunks` : ""}
                </span>
              </div>
              {groupDivision && bunks ? (
                <div className="space-y-2" data-testid={`bunk-list-${g.division}`}>
                  {bunks.map((bunk) => (
                    <BunkAccordion
                      key={bunk.key}
                      divisionName={g.division}
                      bunk={bunk}
                      onOpen={setSelectedId}
                      defaultOpen={Boolean(search && search.trim().length > 0)}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.members.map((m) => (
                    <StaffCardTile key={m.crew_id} m={m} onOpen={setSelectedId} />
                  ))}
                </div>
              )}
            </section>
            );
          })
        )}

        {/* Former Staff (Terminated) section — 2026-07-05 termination redesign.
            Rendered below the active roster with a distinct amber-red heading
            so payroll can find terminated staff quickly. Cards themselves are
            desaturated + carry a red "Terminated" badge. */}
        {terminatedGroups.length > 0 && (
          <section
            className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-4 sm:p-5"
            data-testid="terminated-section"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-base sm:text-lg text-slate-700 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
                Former Staff (Terminated)
              </h2>
              <span
                className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500"
                data-testid="terminated-section-count"
              >
                {terminatedTotal} former {terminatedTotal === 1 ? "staffer" : "staff"}
              </span>
            </div>
            {terminatedGroups.map((g) => (
              <div
                key={g.division}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                data-testid="terminated-grid"
              >
                {g.members.map((m) => (
                  <StaffCardTile key={m.crew_id} m={m} onOpen={setSelectedId} />
                ))}
              </div>
            ))}
          </section>
        )}
      </main>

      {selectedId && <StaffDetailModal crewId={selectedId} onClose={() => { setSelectedId(null); load(search); loadBanner(); }} />}

      {/* Add Custom Staff modal (admin) */}
      {addOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={() => !addSaving && setAddOpen(false)}
          data-testid="add-custom-staff-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3.5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-bold">Manual Entry</div>
                <div className="font-display text-lg font-bold">Add Staff (off-CampMinder)</div>
              </div>
              <button
                onClick={() => !addSaving && setAddOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/10"
                data-testid="add-custom-staff-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-500 leading-relaxed">
                Use this for staff CampMinder doesn&apos;t surface (security
                contractors, one-off hires). These rows are tagged as
                custom and won&apos;t be touched by future syncs.
              </p>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Name *</label>
                <input
                  type="text" value={addName} onChange={(e) => setAddName(e.target.value)}
                  placeholder="Last name, First name"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                  data-testid="add-custom-staff-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Division *</label>
                  <select
                    value={addDivision} onChange={(e) => setAddDivision(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                    data-testid="add-custom-staff-division"
                  >
                    {DIVISION_OPTIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Position</label>
                  <input
                    type="text" value={addPosition} onChange={(e) => setAddPosition(e.target.value)}
                    placeholder="e.g. Security Guard"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                    data-testid="add-custom-staff-position"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Phone</label>
                  <input
                    type="tel" value={addPhone} onChange={(e) => setAddPhone(e.target.value)}
                    placeholder="for kiosk last-4 lookup"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-400 outline-none text-sm font-mono"
                    data-testid="add-custom-staff-phone"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Years at Camp</label>
                  <input
                    type="number" min="0" max="60" value={addYears}
                    onChange={(e) => setAddYears(e.target.value)}
                    placeholder="0 = brand new"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                    data-testid="add-custom-staff-years"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Email</label>
                <input
                  type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="optional"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-400 outline-none text-sm"
                  data-testid="add-custom-staff-email"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setAddOpen(false)} disabled={addSaving}
                  className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-sm font-semibold"
                  data-testid="add-custom-staff-cancel"
                >Cancel</button>
                <button
                  onClick={submitAddCrew} disabled={addSaving}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-sm"
                  data-testid="add-custom-staff-submit"
                >{addSaving ? "Adding…" : "Add Staff"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
